import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Lang, TranslationKey, translate } from '@/i18n'

const STORAGE_KEY = 'chefmax_lang'

function readStoredLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'en' || v === 'zh' || v === 'es') return v
  } catch {}
  return 'es'
}

const INTL_LOCALES: Record<Lang, string> = { es: 'es-MX', en: 'en-US', zh: 'zh-CN' }

interface I18nContextValue {
  lang: Lang
  setLang: (l: Lang) => void
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string
  intlLocale: string
}

const I18nContext = createContext<I18nContextValue>({
  lang: 'es',
  setLang: () => {},
  t: (key) => translate('es', key),
  intlLocale: 'es-MX',
})

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(readStoredLang)

  const setLang = useCallback((l: Lang) => {
    setLangState(l)
    try { localStorage.setItem(STORAGE_KEY, l) } catch {}
  }, [])

  useEffect(() => {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang
  }, [lang])

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => translate(lang, key, vars),
    [lang]
  )

  const intlLocale = INTL_LOCALES[lang]

  const value = useMemo(() => ({ lang, setLang, t, intlLocale }), [lang, setLang, t, intlLocale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => useContext(I18nContext)
